import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useClientData } from '../../contexts/ClientDataContext';
import useStepAccessControl from '../../hooks/useStepAccessControl';
import { showStepBlockedModal } from '../../utils/stepBlockingModals';
import authApi from '../../utils/auth.api';
import LoadingScreen from '../LoadingScreen/LoadingScreen';

/**
 * StepProtectedRoute Component
 *
 * Route guard that enforces step-based access control.
 * Blocks users from accessing steps they haven't unlocked yet.
 *
 * @param {number} requiredStep - Step number required to access this route (0-3)
 * @param {string} redirectTo - Path to redirect to if access is denied (default: '/')
 * @param {boolean} adminBypass - Allow admins to bypass step checks (default: true)
 */
const StepProtectedRoute = ({ requiredStep, redirectTo = '/', adminBypass = true }) => {
  const { clientData } = useClientData();
  const {
    canAccessStep,
    getBlockingReason,
    isBusinessAccount,
    stepBlockingEnabled,
    hasClientData
  } = useStepAccessControl();

  const navigate = useNavigate();
  const user = authApi.getCurrentUser();
  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    // Wait for client data to load
    if (!hasClientData || !clientData) {
      setIsChecking(true);
      return;
    }

    // Admin bypass
    if (adminBypass && (user?.user_type === 'admin' || user?.user_type === 'superadmin')) {
      setHasAccess(true);
      setIsChecking(false);
      return;
    }

    // Business account bypass (if blocking disabled)
    if (isBusinessAccount && !stepBlockingEnabled) {
      setHasAccess(true);
      setIsChecking(false);
      return;
    }

    // Check step access
    const canAccess = canAccessStep(requiredStep);

    if (!canAccess) {
      const reason = getBlockingReason(requiredStep);
      showStepBlockedModal(reason, redirectTo, navigate);
      setHasAccess(false);
      setIsChecking(false);
      return;
    }

    setHasAccess(true);
    setIsChecking(false);
  }, [
    clientData,
    hasClientData,
    requiredStep,
    adminBypass,
    user,
    isBusinessAccount,
    stepBlockingEnabled,
    canAccessStep,
    getBlockingReason,
    redirectTo,
    navigate
  ]);

  // Show loading screen while checking access
  if (isChecking || !hasClientData) {
    return <LoadingScreen />;
  }

  // Render route if access granted
  if (hasAccess) {
    return <Outlet />;
  }

  // Access denied - return null (modal already shown, redirect in progress)
  return null;
};

export default StepProtectedRoute;
